import { useEffect } from 'react';
import Layout from './common/Layout';
import { Grid, Typography, Paper, Box, CircularProgress, Alert } from '@mui/material';
import Card from './common/Card';
import useDashboardStore from '../store/dashboardStore';
import useAdsStore from '../store/adsStore';
import useUserManagementStore from '../store/userManagementStore';
import useOptionConfig from '../hooks/useOptionConfig';
import useProductStore from '../store/productStore';
import useNewUsersPageState from '../hooks/useNewUsersPageState';
import useTempUsersPageState from '../hooks/useTempUsersPageState';
import SchoolIcon from '@mui/icons-material/School';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CampaignIcon from '@mui/icons-material/Campaign';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AttachmentIcon from '@mui/icons-material/Attachment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SettingsIcon from '@mui/icons-material/Settings';
import PaletteIcon from '@mui/icons-material/Palette';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import TableChartIcon from '@mui/icons-material/TableChart';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import QuizIcon from '@mui/icons-material/Quiz';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { usePrivileges } from '../config/privilegeConstant';

const Dashboard = () => {
  const { hasPrivilege, codes } = usePrivileges();
  const { dashboardData, loading, error, fetchDashboardData } = useDashboardStore();
  const { ads, fetchAllAds } = useAdsStore();
  const { users, searchUsers } = useUserManagementStore();
  const {
    users: newUsersList,
    overallStatsCount,
    setFromDate,
    setToDate,
    fetchUsers: fetchNewUsers,
    today
  } = useNewUsersPageState();
  const { tempUsers, fetchTempUsers } = useTempUsersPageState();
  const { allProducts, fetchAllProducts } = useProductStore();
  const { config } = useOptionConfig();
  const labels = config?.labels?.dashboard || {};

  useEffect(() => {
    fetchDashboardData();
    fetchAllAds();
    searchUsers('');
    fetchTempUsers();
    fetchAllProducts();
  }, [fetchDashboardData, fetchAllAds, searchUsers, fetchTempUsers, fetchAllProducts]);

  useEffect(() => {
    const loadTodayStats = () => {
      setFromDate(today);
      setToDate(today);
      setTimeout(() => {
        fetchNewUsers();
      }, 100);
    };
    loadTodayStats();
  }, [fetchNewUsers, today]);

  const cardData = [
    {
      title: labels.totalUsers || 'Total Users',
      content: users?.length.toString() ?? '0',
      icon: <GroupIcon fontSize="large" />,
      link: '/user-management', privilege: codes.VIEW_USERS
    },
    {
      title: labels.tempUsers || 'Temp Users',
      content: tempUsers?.length.toString() ?? '0',
      icon: <PersonOutlineIcon fontSize="large" />,
      link: '/temp-users',
      privilege: codes.TEMP_USERS_VIEW
    },
    {
      title: labels.newUserRegister || 'New Users',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body1">
            {labels.registeredToday || 'Registered Today : '} {newUsersList?.length || 0}
          </Typography>
          <Typography variant="body1">
            {labels.overallAttempts || 'Overall test Attempts : '} {overallStatsCount || 0}
          </Typography>
        </Box>
      ),
      icon: <PersonAddAltIcon fontSize="large" />,
      link: '/new-users',
      privilege: codes.NEW_REGISTERED_USERS
    },
    { title: labels.createTest || 'Create Test', content: labels.createTestDesc || 'Create and manage tests.', icon: <AssignmentIcon fontSize="large" />, link: '/create-test', privilege: codes.CREATE_TEST },
    { title: labels.addQuestion || 'Add Question', content: labels.addQuestionDesc || 'Add new questions to the question bank.', icon: <NoteAddIcon fontSize="large" />, link: '/add-question', privilege: codes.ADD_NEW_QUESTION },
    { title: labels.editQuestions || 'Edit Question', content: labels.editQuestionsDesc || 'Edit existing questions.', icon: <EditIcon fontSize="large" />, link: '/edit-question', privilege: codes.EDIT_QUESTION_VIEW },
    {
      title: labels.availableSubjects || 'Available Subjects',
      content: dashboardData?.availSubjects?.length.toString() ?? '0',
      icon: <SchoolIcon fontSize="large" />
    },
    { title: labels.attachLearningPathway || 'Learning Pathway', content: labels.attachLearningPathwayDesc || 'Upload and manage text, video, or PDFs for a pathway.', icon: <AttachmentIcon fontSize="large" />, link: '/attachments', privilege: codes.VIEW_CONTENT_LP },
    { title: labels.questionBank || 'Question Bank', content: labels.questionBankDesc || 'Manage the entire question bank.', icon: <StorageIcon fontSize="large" />, link: '/question-bank', privilege: codes.VIEW_QB_QUESTIONS },
    { 
      title: labels.products || 'Products', 
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body1">
            {(labels.totalProducts || 'Total Products: ') + (allProducts?.length ?? 0)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {labels.productsDesc || 'Manage your products here'}
          </Typography>
        </Box>
      ),
      icon: <InventoryIcon fontSize="large" />, 
      link: '/products',
      privilege: codes.VIEW_PRODUCT
    },
    { 
      title: labels.orders || 'Orders', 
      content: labels.ordersDesc || 'Track and manage orders.', 
      icon: <ShoppingCartIcon fontSize="large" />, 
      link: '/orders', privilege: codes.VIEW_ORDER 
    },
    { 
      title: labels.mockTestManagement || 'Mock Test Managements', 
      content: labels.mockTestManagementDesc || 'Manage mock tests.', 
      icon: <QuizIcon fontSize="large" />, link: '/mock-test-management', 
      privilege: codes.VIEW_MOCKTEST 
    },
    { 
      title: labels.studentsManagement || 'Registrants', 
      content: labels.studentsManagementDesc || 'Manage Registrations.', 
      icon: <PeopleIcon fontSize="large" />, link: '/students-management', 
      privilege: codes.VIEW_REGISTRATION_STUDENT 
    },
    { title: labels.manageInstitutions || 'Manage Institutions', content: labels.manageInstitutionsDesc || 'Manage the institution.', icon: <AccountBalanceIcon fontSize="large" />, link: '/manage-institutions', privilege: codes.VIEW_INST },
    { title: labels.rankManagement?.dashboardTitle || 'Rank List', content: labels.rankManagement?.dashboardDesc || 'Manage user ranks and permissions.', icon: <WorkspacePremiumIcon fontSize="large" />, link: '/rank-list', privilege: codes.VIEW_RANKS },
    {
      title: labels.adManagement,
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1" component="span">
            {labels.adManagementDesc}
          </Typography>
          <Typography variant="h6" component="span" color="text.secondary">
            {labels.totalAds} : {ads?.length.toString() ?? '0'}
          </Typography>
        </Box>
      ),
      icon: <CampaignIcon fontSize="large" />,
      link: '/ad-service',
      privilege: codes.VIEW_ADS
    },
    { title: labels.configuration || 'Configuration', content: labels.configurationDesc || 'Manage application configuration.', icon: <SettingsIcon fontSize="large" />, link: '/configuration', privilege: codes.VIEW_CONFIGURATIONS },
    { title: labels.colourConfiguration || 'Colour Configuration', content: labels.colourConfigurationDesc || 'Configure application colors.', icon: <PaletteIcon fontSize="large" />, link: '/colour-configuration', privilege: codes.VIEW_COLORS },
    { title: labels.contentEditor || 'Content HTML Editor', content: labels.contentEditorDesc || 'Create and edit content using a rich text editor.', icon: <CodeIcon fontSize="large" />, link: '/html-editor', privilege: codes.VIEW_HTML },
    { title: labels.notifications || 'Notifications & Schedules', content: labels.notificationsDesc || 'Manage push notifications and Quartz schedules.', icon: <NotificationsIcon fontSize="large" />, link: '/notifications', privilege: codes.VIEW_NOTIFICATIONS },
    // { title: labels.excelSequenceChanger || 'Excel Sequence Changer', content: labels.excelSequenceChangerDesc || 'Update questionnaire sequences via Excel upload.', icon: <TableChartIcon fontSize="large" />, link: '/excel-sequence-changer' },   
  ];

  const filteredCardData = cardData.filter(card => {
    if (!card.privilege) return true;
    return hasPrivilege(card.privilege);
  });

  return (
    <Layout>
      <Paper elevation={0} sx={{ p: 3, mb: 4, backgroundColor: 'transparent' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {labels.dashboard || 'Dashboard'}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {labels.welcomeBack || 'Welcome back! Here\'s an overview of your application.'}
        </Typography>
      </Paper>

      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && (
        <Grid container spacing={3}>
          {filteredCardData.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card title={card.title} link={card.link}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {card.icon}
                  <Typography variant={/\d/.test(card.content) ? 'h4' : 'body1'} component="div" sx={{ flexGrow: 1 }}>
                    {card.content}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Layout>
  );
};

export default Dashboard;